import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Package as PackageIcon, ShoppingBag, Check, Download } from "lucide-react";
import { BrandLogo } from "../components/BrandLogo";
import { ShopLanguageSelector } from "../components/ShopLanguageSelector";
import { LogOut } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { PublicImagePreview } from "../components/PublicImagePreview";
import { PublicVideoThumbnail } from "../components/PublicVideoThumbnail";
import { toast } from "sonner";
import { httpBatchLink } from "@trpc/client";
import superjson from "superjson";
import ShopFooter from "../components/ShopFooter";
import { DEFAULT_CURRENCY } from "@shared/const";

export default function GalleryPackageSelection() {
  const [, navigate] = useLocation();
  const { t } = useTranslation();
  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
  const [session, setSession] = useState<any>(null);
  const utils = trpc.useUtils();
  const photographerId = session?.photographerId ?? 0;

  // Load session data
  useEffect(() => {
    try {
      const sessionData = localStorage.getItem("gallery_session");
      if (!sessionData) {
        navigate("/gallery/login");
        return;
      }
      const parsed = JSON.parse(sessionData);
      if (!parsed.projectId || !parsed.token) {
        navigate("/gallery/login");
        return;
      }
      setSession(parsed);
    } catch (e) {
      console.error('[PackageSelection] Failed to parse session:', e);
      navigate("/gallery/login");
    }
  }, [navigate]);

  // All hooks must be called before any conditional returns
  const { data: packages, isLoading } = trpc.packages.listActive.useQuery({
    photographerId,
  }, {
    enabled: photographerId > 0
  });
  const { data: publicSettings } = trpc.settings.getPublicPayPalSettings.useQuery(
    { photographerId },
    { enabled: photographerId > 0 }
  );
  const currency = publicSettings?.currency || DEFAULT_CURRENCY;
  const formatCurrency = (value: string | number) => {
    const numeric = typeof value === "string" ? parseFloat(value || "0") : value;
    return `${numeric.toFixed(2)} ${currency}`;
  };

  // Load project media & purchased IDs to show previously bought items
  const { data: images } = trpc.gallery.getImages.useQuery(
    { projectId: session?.projectId || 0, token: session?.token || "" },
    { enabled: !!session?.projectId && !!session?.token }
  );
  const { data: videos } = trpc.gallery.getVideos.useQuery(
    { projectId: session?.projectId || 0, token: session?.token || "" },
    { enabled: !!session?.projectId && !!session?.token }
  );
  const { data: purchasedImages } = trpc.gallery.getPurchasedImageIds.useQuery(
    { token: session?.token || "" },
    { enabled: !!session?.token }
  );
  const { data: purchasedDigitalImages } = trpc.gallery.getPurchasedDigitalImageIds.useQuery(
    { token: session?.token || "" },
    { enabled: !!session?.token }
  );
  const { data: purchasedVideos } = trpc.gallery.getPurchasedVideoIds.useQuery(
    { token: session?.token || "" },
    { enabled: !!session?.token }
  );
  
  // Mutation to clear cart when changing selection
  const clearCartMutation = trpc.gallery.clearCart.useMutation();
  
  const purchasedSet = new Set<number>(purchasedImages?.imageIds || []);
  const purchasedDigitalSet = new Set<number>(purchasedDigitalImages?.imageIds || []);
  const purchasedVideoSet = new Set<number>(purchasedVideos?.videoIds || []);
  
  // Now we can do conditional rendering after all hooks are called
  if (!session) {
    return null;
  }

  console.log('[Render] Current session:', session);
  console.log('[Render] Images data:', images);
  console.log('[Render] Purchased IDs:', Array.from(purchasedSet));

  const handleDownloadImage = async (imageId: number, filename: string) => {
    if (!session?.token) {
      toast.error(t.downloads.downloadError + " " + filename);
      return;
    }
    try {
      const href = `/api/gallery-download/${encodeURIComponent(session.token)}/image/${imageId}`;
      const link = document.createElement("a");
      link.href = href;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(t.downloads.downloadStarted + " " + filename);
    } catch (e: any) {
      toast.error(e?.message || t.downloads.downloadError + " " + filename);
    }
  };

  const handleDownloadVideo = async (videoId: number, filename: string) => {
    if (!session?.token) {
      toast.error(t.downloads.downloadError + " " + filename);
      return;
    }
    try {
      const href = `/api/gallery-download/${encodeURIComponent(session.token)}/video/${videoId}`;
      const link = document.createElement("a");
      link.href = href;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(t.downloads.downloadStarted + " " + filename);
    } catch (e: any) {
      toast.error(e?.message || t.downloads.downloadError + " " + filename);
    }
  };

  const handleSelectPackage = async (packageId: number | null) => {
    const previousSelected = session.selectedPackageId ?? null;
    const changingSelection = previousSelected !== packageId;
    // If selection changed and we have a cart, clear it first
    if (changingSelection && session.cartId && session.token) {
      try {
        await clearCartMutation.mutateAsync({ cartId: session.cartId, token: session.token });
      } catch (e) {
        console.error('[PackageSelection] Failed to clear cart:', e);
      }
    }

    // Store selected package in session
    const updatedSession = {
      ...session,
      selectedPackageId: packageId,
    };
    localStorage.setItem("gallery_session", JSON.stringify(updatedSession));

    // Navigate to gallery
    navigate("/gallery");
  };

  const getItemDescription = (item: any) => {
    if (item.type === "digital") {
      return t.packages.digitalImages.replace("{count}", item.quantity.toString());
    } else if (item.type === "video") {
      return t.packages.videos.replace("{count}", item.quantity.toString());
    } else if (item.type === "print") {
      const format = item.printFormat ? `${item.printFormat.name}` : t.packages.printFormat;
      return t.packages.physicalPrints
        .replace("{count}", item.quantity.toString())
        .replace("{format}", format);
    }
    return "";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-8 flex flex-col">
      <div className="max-w-6xl mx-auto w-full flex-1">
        <div className="flex justify-between items-center mb-8">
          <BrandLogo size={40} rounded showName />
          <div className="flex items-center gap-3">
            <ShopLanguageSelector />
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => {
                localStorage.removeItem("gallery_session");
                localStorage.removeItem("gallery_login_time");
                navigate("/gallery/login");
              }}
            >
              <LogOut className="h-4 w-4" />
              {t.gallery?.logout || "Logout"}
            </Button>
          </div>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">{t.packages.packageSelection}</h1>
          <p className="text-muted-foreground">{t.packages.selectPackage}</p>
        </div>

        {/* Already Purchased Section moved here */}
        {(purchasedSet.size > 0 || purchasedVideoSet.size > 0) && (
          <div className="mb-12">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              {t.gallery.alreadyPurchased}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {images && images.filter((img: any) => purchasedSet.has(img.id)).map((image: any) => {
                const isDigital = purchasedDigitalSet.has(image.id);
                return (
                  <div key={image.id} className="relative aspect-square bg-white rounded-lg overflow-hidden shadow-sm">
                    <PublicImagePreview
                      imageId={image.id}
                      token={session.token}
                      alt={image.filename}
                      className="w-full h-full"
                    />
                    {isDigital && (
                      <div className="absolute bottom-2 right-2">
                        <Button size="sm" variant="secondary" onClick={() => handleDownloadImage(image.id, image.filename)}>
                          <Download className="h-4 w-4 mr-1" />
                          {t.downloads.download}
                        </Button>
                      </div>
                    )}
                    <div className="absolute top-2 left-2">
                      <Badge variant="secondary">{isDigital ? t.packages.digital : t.packages.print || "Print"}</Badge>
                    </div>
                  </div>
                );
              })}
              {videos && videos.filter((vid: any) => purchasedVideoSet.has(vid.id)).map((video: any) => (
                <div key={video.id} className="relative aspect-video bg-white rounded-lg overflow-hidden shadow-sm">
                  <PublicVideoThumbnail
                    videoId={video.id}
                    token={session.token}
                    alt={video.filename}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-2 right-2">
                    <Button size="sm" variant="secondary" onClick={() => handleDownloadVideo(video.id, video.filename)}>
                      <Download className="h-4 w-4 mr-1" />
                      {t.downloads.download}
                    </Button>
                  </div>
                  <div className="absolute top-2 left-2">
                    <Badge variant="secondary">{t.packages.video}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">{t.common.loading}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Manual Selection Card */}
            <Card 
              className={`cursor-pointer transition-all hover:shadow-lg border-2 ${
                selectedPackageId === null 
                  ? "border-primary shadow-lg" 
                  : "border-border hover:border-primary/50"
              }`}
              onClick={() => handleSelectPackage(null)}
            >
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <ShoppingBag className="w-6 h-6 text-primary" />
                  <CardTitle>{t.packages.manualSelection}</CardTitle>
                </div>
                <CardDescription>
                  {t.packages.manualSelectionDescription}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  className="w-full" 
                  variant={selectedPackageId === null ? "default" : "outline"}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectPackage(null);
                  }}
                >
                  {t.packages.selectThisPackage}
                </Button>
              </CardContent>
            </Card>

            {/* Package Cards */}
            {packages?.map((pkg: any) => (
              <Card
                key={pkg.id}
                className={`cursor-pointer transition-all hover:shadow-lg border-2 ${
                  selectedPackageId === pkg.id
                    ? "border-primary shadow-lg"
                    : "border-border hover:border-primary/50"
                }`}
                onClick={() => setSelectedPackageId(pkg.id)}
              >
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <PackageIcon className="w-6 h-6 text-primary" />
                    <CardTitle>{pkg.name}</CardTitle>
                  </div>
                  {pkg.description && (
                    <CardDescription>{pkg.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm font-semibold mb-2">
                      {t.packages.packageIncludes}
                    </p>
                    <ul className="space-y-1">
                      {pkg.items.map((item: any, idx: number) => (
                        <li key={idx} className="text-sm flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                          {getItemDescription(item)}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="pt-4 border-t">
                    <p className="text-2xl font-bold text-primary">
                      {formatCurrency(pkg.price)}
                    </p>
                  </div>

                  <Button
                    className="w-full"
                    variant={selectedPackageId === pkg.id ? "default" : "outline"}
                    onClick={async (e) => {
                      e.stopPropagation();
                      await handleSelectPackage(pkg.id);
                    }}
                  >
                    {t.packages.selectThisPackage}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!isLoading && (!packages || packages.length === 0) && (
          <Card className="text-center py-12">
            <CardContent>
              <PackageIcon className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground mb-4">{t.packages.noPackages}</p>
              <Button onClick={() => handleSelectPackage(null)}>
                {t.packages.manualSelection}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
      <ShopFooter />
    </div>
  );
}
